import * as THREE from 'three'
import { easing } from 'maath'
import { useRef, Suspense, useEffect } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import {
  Html,
  useProgress,
  CameraControls,
  Environment,
  Bvh,
  useHelper,
  ContactShadows,
} from '@react-three/drei'
import {
  EffectComposer,
  BrightnessContrast,
  N8AO,
  ToneMapping,
} from '@react-three/postprocessing'
import './assets/styles/App.css'
import { useSnapshot } from 'valtio'
import { state } from './context/store'
import Experience from './Components/Experience'
import styled from 'styled-components'
import { BlendFunction, ToneMappingMode } from 'postprocessing'
import { useCameraAutoFocus } from './utilities/useCameraAutoFocus'
// import { useControls } from 'leva'

/*https://gltf.pmnd.rs */

/* files="https://dl.polyhaven.org/file/ph-assets/HDRIs/hdr/1k/potsdamer_platz_1k.hdr" */

const showHelpers = false

export const App = () => {
  // const { color } = useControls({
  //   color: {
  //     value: state.current.choices.topPanel.selected.hex,
  //     onChange: (hex) => {
  //       console.log('leva color value:', hex, typeof hex)
  //       state.current.choices.topPanel.selected.hex = hex
  //       state.current.choices.sidePanels.selected.hex = hex
  //       state.current.choices.midPanels.selected.hex = hex
  //     },
  //   },
  // })

  return (
    <CanvasWrap id="canvas-container">
      <Canvas
        dpr={[1, 1.5]}
        frameloop="demand"
        gl={{
          antialias: true,
          toneMapping: THREE.NoToneMapping,
          toneMappingExposure: 1.25,
          outputColorSpace: THREE.SRGBColorSpace,
        }}
        shadows
        camera={{ position: [2.25, 1, 2.25], fov: 10 }}
      >
        <Scene />
      </Canvas>
    </CanvasWrap>
  )
}

const EnvIntensity = ({ value = 0.3 }) => {
  const { scene } = useThree()
  useEffect(() => {
    scene.environmentIntensity = value
  }, [scene, value])
  return null
}

const Scene = () => {
  const meshRef = useRef()
  const cameraControlsRef = useRef()
  const debugRef = useRef()
  const lightRef = useRef()

  const { camera } = useThree()
  const snap = useSnapshot(state.current)

  // Directional light helper (safe — light is initialised by the time useHelper runs)
  useHelper(showHelpers && lightRef, THREE.DirectionalLightHelper, 1, 'red')

  useEffect(() => {
    if (!showHelpers || !lightRef.current) return
    const light = lightRef.current
    const parent = light.parent
    if (!parent) return

    light.target.position.set(0, 0, 0)
    light.target.updateMatrixWorld()
    parent.add(light.target)

    const helper = new THREE.CameraHelper(light.shadow.camera)
    parent.add(helper)
    return () => {
      parent.remove(helper)
      helper.dispose()
    }
  }, [])

  useEffect(() => {
    const x = state.current.cameraControls.position[0]
    const y = state.current.cameraControls.position[1]
    const z = state.current.cameraControls.position[2]

    const tx = state.current.cameraControls.target[0]
    const ty = state.current.cameraControls.target[1]
    const tz = state.current.cameraControls.target[2]

    cameraControlsRef.current?.dolly(1, true)

    // ✅ fixed bug here (tx not tz)
    cameraControlsRef.current?.setLookAt(x, y, z, tx, ty, tz, true)
  }, [state.current.cameraControls.position])

  useCameraAutoFocus(cameraControlsRef)


  // ✅ debug output
  useFrame(() => {
    if (!cameraControlsRef.current || !debugRef.current) return

    const pos = cameraControlsRef.current.getPosition(new THREE.Vector3())
    const target = cameraControlsRef.current.getTarget(new THREE.Vector3())

    console.log(
      `{position: [${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}], target: [${target.x.toFixed(2)}, ${target.y.toFixed(2)}, ${target.z.toFixed(2)}]}`

    )


    debugRef.current.innerText =
      `pos: ${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}\n` +
      `target: ${target.x.toFixed(2)}, ${target.y.toFixed(2)}, ${target.z.toFixed(2)}`
  })

  return (
    <>
      <ambientLight intensity={1.5} />
      <directionalLight
        ref={lightRef}
        color="white"
        position={[0.6, 4, 0.6]}
        castShadow
        intensity={2}
        shadow-intensity={2}
        shadow-mapSize={[1024, 1024]}
        shadow-bias={-0.0005}
        shadow-normalBias={0.0}
        shadow-radius={4}
        shadow-camera-left={-0.5}
        shadow-camera-right={0.5}
        shadow-camera-top={0.5}
        shadow-camera-bottom={-0.5}
        shadow-camera-near={3.5}
        shadow-camera-far={5}
      />

      <Bvh>
        <Experience />
      </Bvh>

      <EffectComposer autoClear={false} multisampling={4}>
        <N8AO aoRadius={0.03} intensity={3} quality="low" />
        <ToneMapping mode={ToneMappingMode.ACES_FILMIC} />
        <BrightnessContrast brightness={0.02} contrast={0.15} />
      </EffectComposer>

      <CameraControls
        ref={cameraControlsRef}
        smoothTime={0.5}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI / 2.25}
        // minPolarAngle={0}
        // maxPolarAngle={Math.PI / 2.25}
        maxDistance={400}
        minDistance={0.5}
        zoomSpeed={5}
      />

      <Environment
        preset="city"
        background={false}
        environmentIntensity={0.2}
      // resolution={256}
      // blur={0.5}
      />
      {/* <directionalLight position={[-3, 2, -2]} intensity={0.1} /> */}

      {/* ✅ on-screen debug */}
      {/* <Html fullscreen style={{

        pointerEvents: "none",
        zIndex: -1000,
      }}>
        <div
          ref={debugRef}
          style={{
            top: "40vw",
            left: "0px",
            color: "white",
            fontSize: 12,
            background: "rgba(0,0,0,0.6)",
            padding: "6px 8px",
            borderRadius: 4,
            whiteSpace: "pre",
            pointerEvents: "none",
            zIndex: -1000,
          }}
        />
      </Html> */}
    </>
  )
}

export default Canvas

const CanvasWrap = styled.div`
  width: 100vw;

  @media (width > 800px) {
    height: 100%;
    transition: 0.8s all;
  }
`
